import { ServiceType } from '../types';
import type { IconName } from '../components/ui/Icon';

/**
 * Zentrale Konfiguration der Bereiche, in denen die Anwendung läuft
 * (Hauswirtschaft / 24h-Betreuung / Verwaltung). Alles, was sich je Bereich
 * unterscheidet — Akzentfarbe, Navigation, Benennung der Datensätze — steht
 * hier, damit der Rest der Anwendung bereichsneutral bleiben kann.
 */

/** Die Verwaltung: alles rund um die öffentliche Website. */
export const ADMIN_AREA = 'ADMIN' as const;

/**
 * Der Bereich, in dem gearbeitet wird — eines der beiden Geschäftsfelder oder
 * die Verwaltung.
 *
 * Bewusst kein dritter Wert in [ServiceType]: Der hängt an Mitarbeitern, Kunden
 * und Terminen und sagt, welche Leistung erbracht wird. Die Verwaltung erbringt
 * keine; ein Datensatz mit `serviceType = 'ADMIN'` wäre eine Zeile, die es nicht
 * geben darf. Deshalb ein eigener Begriff, der nur die Oberfläche betrifft.
 */
export type AppArea = ServiceType | typeof ADMIN_AREA;

export interface AreaTheme {
  /** Short display label (matches SERVICE_TYPE_LABELS). */
  label: string;
  /** Longer descriptive subtitle, used on the selection screen. */
  tagline: string;
  /** Linien-Icon des Bereichs (aus `components/ui/Icon`). */
  icon: IconName;
  /** Primary accent colour. */
  accent: string;
  /** Darker accent, used for gradients / active states. */
  accentDark: string;
  /** Very light tint for soft backgrounds. */
  soft: string;
  /** Seite, auf der man landet, wenn man in diesen Bereich wechselt. */
  home: string;
}

export const AREA_THEME: Record<AppArea, AreaTheme> = {
  [ServiceType.HOME_CARE]: {
    label: 'Hauswirtschaft',
    tagline: 'Mobile Betreuung mit Tourenplanung – Mitarbeiter besuchen Kunden zu Hause.',
    icon: 'Broom',
    accent: '#4d8c3c',
    accentDark: '#3d7a2e',
    soft: '#eef6ea',
    home: '/dashboard',
  },
  [ServiceType.LIVE_IN_CARE]: {
    label: '24h-Betreuung',
    tagline: 'Live-in-Pflege in Rotationen – Betreuer sind rund um die Uhr bei der Familie.',
    icon: 'Home',
    accent: '#2563eb',
    accentDark: '#1d4ed8',
    soft: '#eaf1fe',
    home: '/dashboard',
  },
  [ADMIN_AREA]: {
    label: 'Verwaltung',
    tagline: 'Die öffentlichen Auftritte betreuen – Anfragen aus den Formularen, die Beiträge der Website und die Zahlen dahinter.',
    icon: 'Globe',
    accent: '#7c3aed',
    accentDark: '#6d28d9',
    soft: '#f3eefe',
    // Kein Dashboard: Die Verwaltung ist eine Arbeitsliste, und die ist der Posteingang.
    home: '/anfragen',
  },
};

/** Mode-specific wording for the two core entities. */
export const ENTITY_LABELS: Record<ServiceType, { employees: string; customers: string }> = {
  [ServiceType.HOME_CARE]: { employees: 'Mitarbeiter', customers: 'Kunden' },
  [ServiceType.LIVE_IN_CARE]: { employees: 'Betreuer', customers: 'Familien' },
};

export interface MenuItem {
  path: string;
  label: string;
  icon: IconName;
  /**
   * Arbeitsbereich, unter dem der Punkt in der Seitenleiste steht. Ohne Gruppe
   * steht er oben ohne Überschrift (Dashboard, Karte).
   */
  group?: string;
  /** Aktiv markieren für alles unterhalb dieses Pfads (sonst: genau `path` und darunter). */
  activePrefix?: string;
}

/**
 * Das Handbuch steht in jedem Bereich unten in der Seitenleiste und öffnet den Teil, der zum
 * Bereich passt — die anderen Teile sind auf der Seite einen Klick entfernt.
 */
const handbuchItem = (teil: string): MenuItem =>
  ({ path: `/handbuch/${teil}`, label: 'Handbuch', icon: 'Book', group: 'Hilfe', activePrefix: '/handbuch' });

/**
 * Die Navigation hängt am Bereich: Die 24h-Betreuung plant keine Touren und
 * benennt Mitarbeiter und Kunden anders, die Verwaltung hat mit den
 * Geschäftsfeldern gar nichts gemein.
 */
export function getMenuItems(area: AppArea, isAdmin: boolean): MenuItem[] {
  if (area === ADMIN_AREA) return verwaltungsMenue(isAdmin);

  const { employees, customers } = ENTITY_LABELS[area];

  const isLiveIn = area === ServiceType.LIVE_IN_CARE;

  // 24h has its own domain (carers/families); Hauswirtschaft uses employees/customers.
  // Die Seitenleiste gruppiert nach Arbeitsbereichen: oben ohne Überschrift der
  // Überblick, dann das Tagesgeschäft, die Daten und die Administration.
  const overview: MenuItem[] = [
    { path: '/dashboard', label: 'Dashboard', icon: 'Dashboard' },
    // Map is used in both fields: tour planning (Hauswirtschaft) and the
    // potential analysis / Potenzialanalyse (24h-Betreuung).
    { path: '/map', label: 'Karte', icon: 'Map' },
  ];

  const TAGESGESCHAEFT = 'Tagesgeschäft';
  const daily: MenuItem[] = [
    { path: isLiveIn ? '/carers' : '/employees', label: employees, icon: 'Users', group: TAGESGESCHAEFT },
    { path: isLiveIn ? '/families' : '/customers', label: customers, icon: isLiveIn ? 'Family' : 'Home', group: TAGESGESCHAEFT },
  ];

  // Field-specific sections: Hauswirtschaft plans visits (Kalender),
  // 24h-Betreuung works leads from the potential analysis (Potenziale).
  const fieldSpecific: MenuItem[] = isLiveIn
    ? [
        { path: '/prospects', label: 'Potenziale', icon: 'Target', group: TAGESGESCHAEFT },
        // Tagespflegen sind Kooperationspartner der 24h-Betreuung: Wer tagsüber dort
        // betreut wird, braucht nachts weiterhin jemanden. In der Hauswirtschaft gibt
        // es diese Beziehung nicht, deshalb steht der Punkt nur im 24h-Menü.
        { path: '/tagespflegen', label: 'Tagespflegen', icon: 'Hospital', group: TAGESGESCHAEFT },
      ]
    : [
        { path: '/calendar', label: 'Kalender', icon: 'Calendar', group: TAGESGESCHAEFT },
        { path: '/vertretungen', label: 'Vertretungen', icon: 'Swap', group: TAGESGESCHAEFT },
      ];

  const tail: MenuItem[] = [
    { path: '/import', label: 'Import/Export', icon: 'ImportExport', group: 'Daten' },
  ];

  const adminItems: MenuItem[] = [
    { path: '/users', label: 'Benutzerverwaltung', icon: 'User', group: 'Administration' },
    { path: '/settings', label: 'Einstellungen', icon: 'Settings', group: 'Administration' },
  ];

  const items = [...overview, ...daily, ...fieldSpecific, ...tail];
  const hilfe = handbuchItem(isLiveIn ? '24h-betreuung' : 'hauswirtschaft');

  return isAdmin ? [...items, ...adminItems, hilfe] : [...items, hilfe];
}

/**
 * Die Verwaltung bündelt, was zur Website gehört und keinem Geschäftsfeld: Der
 * Posteingang betrifft beide — wer über ein Formular schreibt, entscheidet sich
 * erst im Gespräch für eine Leistung — und die Beiträge betreffen keins.
 */
function verwaltungsMenue(isAdmin: boolean): MenuItem[] {
  const items: MenuItem[] = [{ path: '/anfragen', label: 'Anfragen', icon: 'Inbox' }];

  // Die Beiträge der öffentlichen Website — wie die Texte der Seite nur für
  // Administratoren, weil sie nach außen wirken. Die Auswertung ebenso: Sie
  // ist das, was der Kunde über den Erfolg seiner Website erfährt.
  const hilfe = handbuchItem('grundlagen');
  return isAdmin
    ? [
        ...items,
        { path: '/website-beitraege', label: 'Website-Beiträge', icon: 'Newspaper', group: 'Website' },
        { path: '/auswertung', label: 'Auswertung', icon: 'Chart', group: 'Website' },
        hilfe,
      ]
    : [...items, hilfe];
}
